import React, { useState, useEffect, useCallback } from 'react';
import EmailHistoryPanel from './EmailHistoryPanel';
import RichBodyEditor from './RichBodyEditor';

const fmtDate = (d) => d
  ? new Date(d).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
  : '—';

// Strips HTML down to plain text for a one-line preview — good enough for
// a list snippet, not meant to be a safe render of the full body (the
// expanded view below does that properly via dangerouslySetInnerHTML).
const snippet = (html, text, max = 140) => {
  const raw = text || (html || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  return raw.length > max ? `${raw.slice(0, max)}…` : raw;
};

// Every inbound email captured across every community — see GET
// /admin/inbox. Global (not community-scoped): a "cold" email to some
// address has no reliable community link until it's a reply to something
// tracked, so there's no honest way to filter this list by community.
const InboxPanel = ({ authedFetch }) => {
  const [emails, setEmails] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [viewingHistoryUserId, setViewingHistoryUserId] = useState(null);
  const [replyingId, setReplyingId] = useState(null);
  const [replySubject, setReplySubject] = useState('');
  const [replyBody, setReplyBody] = useState('');
  const [includeSignature, setIncludeSignature] = useState(true);
  const [sendAsMe, setSendAsMe] = useState(true);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState('');

  const fetchInbox = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await authedFetch('/inbox?limit=200');
      setEmails(data.emails || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [authedFetch]);

  useEffect(() => { fetchInbox(); }, [fetchInbox]);

  const startReply = (e) => {
    setReplyingId(e._id);
    setReplySubject(e.subject ? (/^re:/i.test(e.subject) ? e.subject : `Re: ${e.subject}`) : '');
    setReplyBody('');
    setSendError('');
  };

  const cancelReply = () => {
    setReplyingId(null);
    setSendError('');
  };

  const handleSendReply = async (emailId) => {
    if (!replyBody.trim()) {
      setSendError('Write a reply first');
      return;
    }
    setSending(true);
    setSendError('');
    try {
      const result = await authedFetch(`/inbox/${emailId}/reply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ subject: replySubject, htmlBody: replyBody, includeSignature, sendAsMe }),
      });
      if (result.status === 'failed') {
        setSendError(result.errorMessage || 'Send failed');
        return;
      }
      setEmails((prev) => prev.map((e) => (
        e._id === emailId ? { ...e, repliedAt: result.repliedAt, repliedByAdmin: result.repliedByAdmin } : e
      )));
      setReplyingId(null);
    } catch (err) {
      setSendError(err.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <div>
      {error && <div className="as-error">{error}</div>}

      <div className="as-activity-toolbar">
        <button className="as-filter-tab" onClick={fetchInbox}>↻ Refresh</button>
      </div>

      {loading && <div className="as-loading">Loading…</div>}
      {emails && emails.length === 0 && (
        <div className="as-empty">
          No inbound emails captured yet. If you've just set this up, confirm Brevo's Inbound Parse Webhook is configured to route mail to this app.
        </div>
      )}

      {emails && emails.length > 0 && (
        <div className="as-signup-list">
          {emails.map((e) => (
            <div key={e._id} className="as-signup-card" style={{ cursor: 'pointer', display: 'block' }} onClick={() => setExpandedId(expandedId === e._id ? null : e._id)}>
              <div style={{ width: '100%' }}>
                <div className="as-signup-headline">
                  <strong>{e.fromEmail || '(unknown sender)'}</strong>
                  {e.communityTitle && <span className="as-card-meta"> · {e.communityTitle}</span>}
                  {!e.isTrackedReply && <span className="as-card-meta"> · not a tracked reply</span>}
                </div>
                <div className="as-card-meta">to {e.toEmail} · {fmtDate(e.receivedAt)}</div>
                <div className="as-card-meta"><strong>{e.subject || '(no subject)'}</strong></div>
                {e.repliedAt && <div className="as-card-meta">✅ Replied {fmtDate(e.repliedAt)}{e.repliedByAdmin ? ` by ${e.repliedByAdmin}` : ''}</div>}
                {expandedId !== e._id && <div className="as-card-meta">{snippet(e.htmlBody, e.textBody)}</div>}

                {expandedId === e._id && (
                  <>
                    {e.htmlBody ? (
                      <div className="as-suggestion-body-preview" dangerouslySetInnerHTML={{ __html: e.htmlBody }} />
                    ) : (
                      <div className="as-suggestion-body-preview">{e.textBody || '(empty body)'}</div>
                    )}
                    <div className="as-email-actions" onClick={(ev) => ev.stopPropagation()}>
                      {e.recipientUserId && (
                        <button
                          type="button"
                          className="as-text-link"
                          onClick={() => setViewingHistoryUserId(e.recipientUserId)}
                        >
                          View email history for this person
                        </button>
                      )}
                      {replyingId !== e._id && (
                        <button type="button" className="as-filter-tab" onClick={() => startReply(e)}>
                          ↩️ Reply
                        </button>
                      )}
                    </div>

                    {replyingId === e._id && (
                      <div className="as-email-compose" onClick={(ev) => ev.stopPropagation()}>
                        {sendError && <div className="as-error">{sendError}</div>}
                        <div className="as-email-field">
                          <label>To</label>
                          <input type="text" value={e.fromEmail || ''} disabled />
                        </div>
                        <div className="as-email-field">
                          <label>Subject</label>
                          <input type="text" value={replySubject} onChange={(ev) => setReplySubject(ev.target.value)} />
                        </div>
                        <div className="as-email-field">
                          <label>Reply</label>
                          <RichBodyEditor value={replyBody} onChange={setReplyBody} placeholder="Write your reply..." />
                        </div>
                        <div className="as-activity-toolbar">
                          <label className="as-suggestion-checkbox-label">
                            <input type="checkbox" checked={sendAsMe} onChange={(ev) => setSendAsMe(ev.target.checked)} />
                            Send as me
                          </label>
                          <label className="as-suggestion-checkbox-label">
                            <input type="checkbox" checked={includeSignature} onChange={(ev) => setIncludeSignature(ev.target.checked)} />
                            Add my signature
                          </label>
                        </div>
                        <div className="as-email-actions">
                          <button type="button" className="as-btn as-btn--approve" onClick={() => handleSendReply(e._id)} disabled={sending}>
                            {sending ? 'Sending…' : '📤 Send Reply'}
                          </button>
                          <button type="button" className="as-filter-tab" onClick={cancelReply} disabled={sending}>
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {viewingHistoryUserId && (
        <EmailHistoryPanel userId={viewingHistoryUserId} onClose={() => setViewingHistoryUserId(null)} />
      )}
    </div>
  );
};

export default InboxPanel;
